(() => {
  if (window.CMAutumnHero) {
    window.CMAutumnHero.initAll(document);
    return;
  }

  const instances = new WeakMap();
  const assetLoads = new Map();
  const mobileQuery = window.matchMedia('(max-width: 800px)');
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const preloadAsset = (src) => {
    if (!src) return Promise.resolve(true);
    if (assetLoads.has(src)) return assetLoads.get(src);

    const promise = new Promise((resolve) => {
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => {
        if (typeof image.decode === 'function') {
          image.decode().catch(() => {}).finally(() => resolve(true));
          return;
        }
        resolve(true);
      };
      image.onerror = () => resolve(false);
      image.src = src;
      if (image.complete && image.naturalWidth > 0) resolve(true);
    });

    assetLoads.set(src, promise);
    return promise;
  };

  const init = (root) => {
    if (!(root instanceof HTMLElement) || instances.has(root)) return;

    const hero = root.querySelector('.cm-autumn__hero');
    const slides = Array.from(root.querySelectorAll('.cm-autumn__slide'));
    const controls = Array.from(root.querySelectorAll('.cm-autumn__number'));
    const status = root.querySelector('.cm-autumn__status');
    const autoplayButton = root.querySelector('.cm-autumn__autoplay');
    if (!hero || slides.length === 0) return;

    let current = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let requestToken = 0;
    let pointerStart = null;
    let timer = 0;
    let paused = false;
    let destroyed = false;
    const autoplaySeconds = Number(root.dataset.autoplaySeconds) || 0;
    const listeners = [];

    const on = (target, type, handler, options) => {
      target.addEventListener(type, handler, options);
      listeners.push(() => target.removeEventListener(type, handler, options));
    };

    const activeTone = () => {
      const slide = slides[current];
      if (!slide) return 'light';
      return mobileQuery.matches
        ? slide.dataset.headerTone || 'light'
        : slide.dataset.desktopHeaderTone || 'light';
    };

    const publishTone = () => {
      const tone = activeTone();
      root.dataset.headerTone = tone;
      hero.dataset.headerTone = tone;
      root.dispatchEvent(new CustomEvent('cm-autumn:change', {
        bubbles: true,
        detail: {
          sectionId: root.dataset.sectionId,
          index: current,
          headerTone: tone,
          slide: slides[current]
        }
      }));
    };

    const updateStatus = (announce) => {
      if (!status) return;
      if (!announce) {
        status.textContent = '';
        return;
      }
      const title = slides[current].querySelector('.cm-autumn__title')?.textContent?.trim() || '';
      status.textContent = `${current + 1} / ${slides.length}${title ? ` ${title}` : ''}`;
    };

    const clearTimer = () => {
      if (!timer) return;
      window.clearTimeout(timer);
      timer = 0;
    };

    const schedule = () => {
      clearTimer();
      if (
        destroyed ||
        paused ||
        autoplaySeconds <= 0 ||
        slides.length < 2 ||
        reduceMotionQuery.matches ||
        document.hidden
      ) return;
      timer = window.setTimeout(() => {
        activate(current + 1, false);
      }, autoplaySeconds * 1000);
    };

    const commit = (nextIndex, announce) => {
      current = nextIndex;
      slides.forEach((slide, index) => {
        const active = index === current;
        slide.classList.toggle('is-active', active);
        slide.hidden = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });
      controls.forEach((control, index) => {
        control.setAttribute('aria-current', String(index === current));
      });
      root.removeAttribute('aria-busy');
      delete root.dataset.loadError;
      publishTone();
      updateStatus(announce);
      schedule();
    };

    const activate = (requestedIndex, announce = true) => {
      const nextIndex = ((requestedIndex % slides.length) + slides.length) % slides.length;
      const token = ++requestToken;
      if (nextIndex === current) {
        root.removeAttribute('aria-busy');
        publishTone();
        schedule();
        return;
      }

      const target = slides[nextIndex];
      const src = mobileQuery.matches
        ? target.dataset.mobileSrc || target.dataset.desktopSrc
        : target.dataset.desktopSrc || target.dataset.mobileSrc;
      root.setAttribute('aria-busy', 'true');

      const targetImage = target.querySelector('.cm-autumn__image');
      if (targetImage) targetImage.loading = 'eager';
      preloadAsset(src).then((loaded) => {
        if (destroyed || token !== requestToken) return;
        if (!loaded) {
          root.removeAttribute('aria-busy');
          root.dataset.loadError = 'true';
          schedule();
          return;
        }
        commit(nextIndex, announce);
      });
    };

    controls.forEach((control) => {
      on(control, 'click', () => {
        activate(Number(control.dataset.slideTarget), true);
      });
    });

    on(hero, 'keydown', (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') activate(0, true);
      if (event.key === 'End') activate(slides.length - 1, true);
      if (event.key === 'ArrowLeft') activate(current - 1, true);
      if (event.key === 'ArrowRight') activate(current + 1, true);
    });

    on(hero, 'pointerdown', (event) => {
      if (event.target.closest('a, button')) return;
      pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
    });

    on(hero, 'pointerup', (event) => {
      if (!pointerStart || pointerStart.id !== event.pointerId) return;
      const deltaX = event.clientX - pointerStart.x;
      const deltaY = event.clientY - pointerStart.y;
      pointerStart = null;
      if (Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.3) {
        activate(current + (deltaX < 0 ? 1 : -1), true);
      }
    });

    on(hero, 'pointercancel', () => {
      pointerStart = null;
    });

    on(hero, 'mouseenter', clearTimer);
    on(hero, 'mouseleave', schedule);
    on(hero, 'focusin', clearTimer);
    on(hero, 'focusout', (event) => {
      if (!hero.contains(event.relatedTarget)) schedule();
    });

    if (autoplayButton) {
      on(autoplayButton, 'click', () => {
        paused = !paused;
        autoplayButton.setAttribute('aria-pressed', String(paused));
        autoplayButton.textContent = paused ? '再生' : '一時停止';
        if (paused) clearTimer();
        else schedule();
      });
    }

    const handleViewportChange = () => {
      requestToken += 1;
      root.removeAttribute('aria-busy');
      publishTone();
      schedule();
    };

    const handleVisibility = () => {
      if (document.hidden) clearTimer();
      else schedule();
    };

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', handleViewportChange);
      listeners.push(() => mobileQuery.removeEventListener('change', handleViewportChange));
    } else {
      mobileQuery.addListener(handleViewportChange);
      listeners.push(() => mobileQuery.removeListener(handleViewportChange));
    }

    if (typeof reduceMotionQuery.addEventListener === 'function') {
      reduceMotionQuery.addEventListener('change', schedule);
      listeners.push(() => reduceMotionQuery.removeEventListener('change', schedule));
    } else {
      reduceMotionQuery.addListener(schedule);
      listeners.push(() => reduceMotionQuery.removeListener(schedule));
    }

    on(document, 'visibilitychange', handleVisibility);
    publishTone();
    updateStatus(false);
    schedule();

    instances.set(root, {
      destroy() {
        destroyed = true;
        requestToken += 1;
        clearTimer();
        listeners.splice(0).forEach((remove) => remove());
        instances.delete(root);
      }
    });
  };

  const initAll = (scope) => {
    if (scope instanceof HTMLElement && scope.matches('.cm-autumn')) init(scope);
    scope.querySelectorAll?.('.cm-autumn').forEach(init);
  };

  const handleSectionLoad = (event) => initAll(event.target);
  const handleSectionUnload = (event) => {
    const root = event.target.matches?.('.cm-autumn')
      ? event.target
      : event.target.querySelector?.('.cm-autumn');
    instances.get(root)?.destroy();
  };

  window.CMAutumnHero = { initAll };
  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }
})();
